import { ChallengeTimeUnit, ChallengeType } from '../../graphql/types';

export type GoalPeriod = 'Total' | 'Daily' | 'Weekly';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Inclusive count of days between the dates, or null if they are missing or invalid.
export const getDayCount = (
  startDate?: string,
  endDate?: string,
): number | null => {
  if (!startDate || !endDate) {
    return null;
  }
  const days =
    Math.round((Date.parse(endDate) - Date.parse(startDate)) / MS_PER_DAY) + 1;
  return Number.isFinite(days) && days >= 1 ? days : null;
};

// Activity count, step count and hour goals are whole numbers; other goals
// (e.g. miles) may be fractional.
export const isWholeNumberGoal = (
  challengeType?: string,
  timeUnit: string = ChallengeTimeUnit.Hours,
): boolean =>
  challengeType === ChallengeType.ClassCount ||
  challengeType === ChallengeType.StepCount ||
  (challengeType === ChallengeType.Time &&
    timeUnit === ChallengeTimeUnit.Hours);

// Converts a goal between periods via its per-day equivalent. Returns null
// until the goal and both dates are valid. Rounds to a whole number when
// roundToWhole is set, otherwise to 2 decimals.
export const convertGoal = (
  goal: number,
  from: GoalPeriod,
  to: GoalPeriod,
  startDate?: string,
  endDate?: string,
  roundToWhole = false,
): number | null => {
  const days = getDayCount(startDate, endDate);
  if (!Number.isFinite(goal) || days === null) {
    return null;
  }
  const perDay = {
    Total: goal / days,
    Daily: goal,
    Weekly: goal / 7,
  }[from];
  const converted = {
    Total: perDay * days,
    Daily: perDay,
    Weekly: perDay * 7,
  }[to];
  return roundToWhole
    ? Math.round(converted)
    : Math.round(converted * 100) / 100;
};

// Replaces the entered goal with the total goal and drops the form-only
// goalPeriod field so it isn't sent to the API.
export const transformGoalPeriod = ({
  goalPeriod,
  ...data
}: Record<string, any>): Record<string, any> => {
  if (!goalPeriod || goalPeriod === 'Total') {
    return data;
  }
  const totalGoal = convertGoal(
    Number(data.goal),
    goalPeriod,
    'Total',
    data.startDate,
    data.endDate,
    isWholeNumberGoal(data.challengeType, data.timeUnit),
  );
  return totalGoal === null ? data : { ...data, goal: totalGoal };
};
